/**
 * MyDeskPlanRoom — cross-project Plan Room window (Wave 5.8).
 *
 * Two-tier pattern (like Punch): this is the My Desk surface. It surfaces
 * every project's plan-room contents as thumbnail tiles, supports quick
 * intake (drop / browse), and links through to each project's Plan Room
 * (the register — source of truth).
 *
 * Intake rule: quick-dropped files land as UNFILED in the target project's
 * plan room. They are visible + thumbnailed but NOT issued/current. The
 * user must explicitly file them into an issued set to promote them.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload, FileText, ChevronRight, PackageCheck, Inbox, ArrowRight, X, FilePlus2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import DrawingCard from '@/components/DrawingCard';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import RulebookShortcut from '@/components/rulebook/RulebookShortcut';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyDeskPlanRoom({ darkMode = false }) {
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();
  const [projects, setProjects] = useState([]);
  const [tenantUnfiled, setTenantUnfiled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());       // project ids
  const [dropTargetProject, setDropTargetProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [showFileDialog, setShowFileDialog] = useState(null); // {projectId?, drawing?, parsed?, unfiledId?}
  const [dragActive, setDragActive] = useState(false);
  // Build 13.6 — search + filters for the cross-project window.
  const [filterProject, setFilterProject] = useState('all');
  const [filterDiscipline, setFilterDiscipline] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchText, setSearchText] = useState('');
  const fileBrowseRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [rProj, rUnfiled] = await Promise.all([
        axios.get(`${API}/plan-room/my-desk`),
        axios.get(`${API}/plan-room/unfiled`).catch(() => ({ data: { unfiled: [] } })),
      ]);
      setProjects(rProj.data.projects || []);
      setTenantUnfiled(rUnfiled.data.unfiled || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API}/plan-room/phases`).then(r => setPhases(r.data.phases || [])).catch(() => {});
  }, [load]);

  const toggle = (pid) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(pid)) n.delete(pid); else n.add(pid);
      return n;
    });
  };

  // ---- Intake handling ----
  const handleFilesForProject = async (projectId, files) => {
    if (!files || !files.length) return;
    const results = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await axios.post(
          `${API}/projects/${projectId}/plan-room/intake`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        results.push(r.data);
      } catch (e) {
        toast.error(`Failed to upload ${f.name}: ${e?.response?.data?.detail || 'error'}`);
      }
    }
    if (results.length) {
      toast.success(`Intake complete — ${results.length} file(s) added as unfiled`);
      await load();
      // If any file parsed cleanly, offer to file-into-set immediately.
      const firstParsed = results.find(r => r?.parsed?.set_name);
      if (firstParsed) {
        setShowFileDialog({
          projectId,
          drawing: firstParsed.drawing,
          parsed: firstParsed.parsed,
        });
      }
    }
  };

  // Build 13.6 — tenant-scoped upload (no project pick). Files land in
  // `unfiled_plan_room_items` and surface in the top Unfiled band.
  const handleTenantUnfiledUpload = async (files) => {
    if (!files || !files.length) return;
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        await axios.post(`${API}/plan-room/unfiled`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch (e) {
        const reason = e?.response?.data?.detail || e?.message || 'upload failed';
        toast.error(`${f.name}: ${reason}`);
      }
    }
    if (ok) {
      toast.success(`${ok} file(s) landed in Unfiled — promote to file into a project`);
      await load();
    }
  };

  // Top-level drop / browse: land in TENANT-SCOPED Unfiled (Build 13.6).
  const onGlobalDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) handleTenantUnfiledUpload(files);
  };

  const onBrowseSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) handleTenantUnfiledUpload(files);
    e.target.value = '';
  };

  const deleteTenantUnfiled = async (id) => {
    try {
      await axios.delete(`${API}/plan-room/unfiled/${id}`);
      setTenantUnfiled(prev => prev.filter(x => x.id !== id));
      toast.success('Removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const onProjectDrop = (projectId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetProject(null);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) handleFilesForProject(projectId, files);
  };

  const totalUnfiled = projects.reduce((n, p) => n + (p.unfiled_count || 0), 0) + tenantUnfiled.length;

  return (
    <div
      className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#FAF9F7] text-[#1A1918]'}`}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onGlobalDrop}
      data-testid="my-desk-plan-room"
    >
      <header className={`sticky top-0 z-10 border-b ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'}`}>
        {/* Stage D ride-along — breadcrumb: My Desk / Plans */}
        <nav
          aria-label="Breadcrumb"
          className={`max-w-[1600px] mx-auto px-6 md:px-12 pt-3 flex items-center gap-1.5 text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}
          data-testid="my-desk-plan-room-breadcrumb"
        >
          <button
            type="button"
            onClick={() => navigate('/')}
            className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : 'hover:text-[#1A1918]'}`}
            data-testid="breadcrumb-my-desk"
          >
            My Desk
          </button>
          <ChevronRight className="w-3 h-3 opacity-60" />
          <span className={darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'} data-testid="breadcrumb-plans">
            Plans
          </span>
        </nav>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PackageCheck className={`w-6 h-6 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`} />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading font-light text-xl" data-testid="my-desk-plan-room-title">
                  Plan Room
                </h1>
                <RulebookShortcut nodeId="my-desk.plans" />
              </div>
              <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                {projects.length} project{projects.length === 1 ? '' : 's'} · {totalUnfiled} unfiled across all projects
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => fileBrowseRef.current?.click()}
              data-testid="my-desk-plan-room-browse-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Browse & upload
            </Button>
            <input
              ref={fileBrowseRef} type="file" multiple accept=".pdf,image/*"
              className="hidden" onChange={onBrowseSelect}
            />
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-6">
        <div
          className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive
            ? (darkMode ? 'border-[#BC5D41] bg-[#1A1A1A]' : 'border-[#70170F] bg-[#FFF4F0]')
            : (darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]')}`}
          data-testid="my-desk-plan-room-dropzone"
        >
          <Upload className={`mx-auto mb-2 w-8 h-8 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`} />
          <p className={`text-sm ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
            Drop drawing files anywhere on this page — they land as <b>Unfiled</b> (tenant-wide).
          </p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
            Promote an Unfiled item to a project&apos;s issued register with a deliberate step (assign project + rev + phase).
          </p>
        </div>

        {/* Build 13.6 — Tenant-scoped Unfiled band at TOP */}
        {tenantUnfiled.length > 0 && (
          <section
            className={`mb-6 rounded-lg border p-4 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-[#FFF9F0] border-[#F0DFBF]'}`}
            data-testid="tenant-unfiled-band"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Inbox className={`w-5 h-5 ${darkMode ? 'text-[#BC5D41]' : 'text-[#B77317]'}`} />
                <h2 className="font-heading text-lg" data-testid="tenant-unfiled-band-title">
                  Unfiled — {tenantUnfiled.length}
                </h2>
              </div>
              <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                Captured drawings not yet in any project register. Promote them below.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {tenantUnfiled.map(uf => (
                <div key={uf.id} className={`relative rounded-lg overflow-hidden border ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`} data-testid={`unfiled-tile-${uf.id}`}>
                  <div className={`aspect-[4/3] flex items-center justify-center relative ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F3EF]'}`}>
                    <DrawingCard
isSuperAdmin={isSuperAdmin}
                      drawing={{
                        id: uf.id,
                        thumbnail_url: uf.thumb_url,
                        current_file_url: uf.file_url,
                        current_version_id: uf.id,
                      }}
                      testId={`unfiled-tile-thumb-${uf.id}`}
                      className="w-full h-full object-cover"
                      fallbackIconClassName={`w-8 h-8 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#B0AEAB]'}`}
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate" title={uf.title}>{uf.title}</p>
                    <p className={`text-[10px] ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                      {uf.source === 'email' ? `From ${uf.source_email?.sender || 'email'}` : 'Uploaded'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 p-2 pt-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-1"
                      onClick={() => setShowFileDialog({ unfiledId: uf.id, parsed: uf.parsed_filename || {}, title: uf.title })}
                      data-testid={`unfiled-promote-${uf.id}`}>
                      <ArrowRight className="w-3 h-3 mr-1" /> Promote
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => deleteTenantUnfiled(uf.id)}
                      data-testid={`unfiled-delete-${uf.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Build 13.6 — search + filter row */}
        <div className={`mb-4 flex flex-wrap items-center gap-2 rounded-lg border p-2 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'}`} data-testid="my-desk-plan-room-filters">
          <Input
            placeholder="Search drawings — number, title, filename…"
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
            className="max-w-xs h-8 text-xs" data-testid="my-desk-plan-room-search"
          />
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="my-desk-plan-room-project-filter"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="my-desk-plan-room-disc-filter"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All disciplines</SelectItem>
              {['A','S','M','E','P','FP','C','L','I'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="my-desk-plan-room-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="plan">Plan</SelectItem>
              <SelectItem value="elevation">Elevation</SelectItem>
              <SelectItem value="section">Section</SelectItem>
              <SelectItem value="detail">Detail</SelectItem>
              <SelectItem value="schedule">Schedule</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-16 text-sm text-muted-foreground">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground">
            No projects yet. Create a project first, then come back to intake drawings.
          </div>
        ) : (
          <div className="space-y-2">
            {projects
              .filter(p => filterProject === 'all' || p.project_id === filterProject)
              .map(p => {
                // Apply drawing-level filters at the row level. Row hides itself
                // if it has zero matching drawings after filter.
                const filteredDrawings = (p.drawings || []).filter(d => {
                  if (filterDiscipline !== 'all' && (d.discipline || '').toUpperCase() !== filterDiscipline.toUpperCase()) return false;
                  if (filterType !== 'all' && (d.type || '').toLowerCase() !== filterType.toLowerCase()) return false;
                  if (searchText.trim()) {
                    const q = searchText.toLowerCase();
                    const hay = `${d.drawing_number || ''} ${d.title || ''} ${d.filename || ''}`.toLowerCase();
                    if (!hay.includes(q)) return false;
                  }
                  return true;
                });
                const anyFilterActive = filterDiscipline !== 'all' || filterType !== 'all' || !!searchText.trim();
                if (anyFilterActive && filteredDrawings.length === 0) return null;
                const filteredProj = { ...p, drawings: filteredDrawings };
                return (
                  <ProjectRow
                    key={p.project_id}
                    data={filteredProj}
                    open={expanded.has(p.project_id)}
                    onToggle={() => toggle(p.project_id)}
                    darkMode={darkMode}
                    onDrop={(e) => onProjectDrop(p.project_id, e)}
                    onDragOver={(e) => { e.preventDefault(); setDropTargetProject(p.project_id); }}
                    onDragLeave={() => setDropTargetProject(null)}
                    isTarget={dropTargetProject === p.project_id}
                    onOpenProject={() => navigate(`/projects/${p.project_id}/drawings`)}
                    onFile={(drawing, parsed) => setShowFileDialog({ projectId: p.project_id, drawing, parsed })}
                    onReload={load}
                  />
                );
              })}
          </div>
        )}
      </main>

      {showFileDialog && (
        <FileIntoSetDialog
          {...showFileDialog}
          phases={phases}
          projects={projects}
          onClose={() => setShowFileDialog(null)}
          onDone={async () => {
            setShowFileDialog(null);
            await load();
            toast.success('Filed into set');
          }}
        />
      )}
    </div>
  );
}


function ProjectRow({ data, open, onToggle, darkMode, onDrop, onDragOver, onDragLeave, isTarget, onOpenProject, onFile, onReload }) {
  const isSuperAdmin = useIsSuperAdmin();
  const [unfiled, setUnfiled] = useState([]);
  const [loadingUnfiled, setLoadingUnfiled] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoadingUnfiled(true);
    axios.get(`${API}/projects/${data.project_id}/plan-room/unfiled`)
      .then(r => setUnfiled(r.data.drawings || []))
      .catch(() => {})
      .finally(() => setLoadingUnfiled(false));
  }, [open, data.project_id]);

  return (
    <div
      className={`border rounded-lg transition-colors ${isTarget
        ? (darkMode ? 'border-[#BC5D41] bg-[#1A1A1A]' : 'border-[#70170F] bg-[#FFF4F0]')
        : (darkMode ? 'border-[#2A2A2A] bg-[#141414]' : 'border-[#E5E2DD] bg-white')}`}
      onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
      data-testid={`my-desk-plan-room-project-${data.project_id}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className={`w-full flex items-center justify-between px-4 py-3 cursor-pointer ${darkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-gray-50'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
          <div className="text-left min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium truncate">{data.project_name}</h3>
              {data.latest_set && (
                <Badge variant="outline" className="text-[10px]">
                  {data.latest_set.name} · {data.latest_set.phase} · {data.latest_set.issue_date}
                </Badge>
              )}
              {data.unfiled_count > 0 && (
                <Badge className="text-[10px] bg-[#70170F] text-white">{data.unfiled_count} unfiled</Badge>
              )}
            </div>
            <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
              {data.drawings_count} drawing{data.drawings_count === 1 ? '' : 's'}
              {data.client_name ? ` · ${data.client_name}` : ''}
            </p>
          </div>
        </div>
        <Button
          size="sm" variant="ghost"
          onClick={(e) => { e.stopPropagation(); onOpenProject(); }}
          data-testid={`my-desk-plan-room-open-${data.project_id}-btn`}
        >
          Open <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>

      {open && (
        <div className={`px-4 pb-4 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}>
          {/* Unfiled */}
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-2">
              <Inbox className={`w-4 h-4 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`} />
              <h4 className="text-xs uppercase tracking-wide font-semibold">Unfiled ({unfiled.length})</h4>
            </div>
            {loadingUnfiled ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : unfiled.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nothing unfiled. Drop files here or use the Browse button.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {unfiled.map(d => (
                  <UnfiledTile
                    key={d.id} drawing={d} darkMode={darkMode}
                    onFile={() => onFile(d, null)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Recent (mix of filed + unfiled) */}
          {(data.recent_drawings || []).length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs uppercase tracking-wide font-semibold mb-2">Recent</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {(data.recent_drawings || []).map(d => (
                  <button
                    key={d.id}
                    onClick={onOpenProject}
                    className={`text-left rounded overflow-hidden border ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-[#FAF9F7]'}`}
                    data-testid={`my-desk-plan-room-recent-${d.id}`}
                  >
                    <div className="aspect-[4/5] bg-gray-100 flex items-center justify-center relative">
                      <DrawingCard
                        isSuperAdmin={isSuperAdmin}
                        drawing={d}
                        testId={`my-desk-plan-room-recent-thumb-${d.id}`}
                        className="w-full h-full object-cover"
                        fallbackIconClassName="w-8 h-8 text-gray-400"
                      />
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-mono truncate">{d.drawing_number || '—'}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{d.title}</p>
                      {d.is_unfiled && <Badge className="text-[9px] mt-1 bg-[#70170F] text-white">unfiled</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function UnfiledTile({ drawing, darkMode, onFile }) {
  const isSuperAdmin = useIsSuperAdmin();
  return (
    <div
      className={`rounded overflow-hidden border ${darkMode ? 'border-[#BC5D41]/50 bg-[#1A1A1A]' : 'border-[#70170F]/40 bg-white'}`}
      data-testid={`my-desk-plan-room-unfiled-${drawing.id}`}
    >
      <div className="aspect-[4/5] bg-gray-100 flex items-center justify-center relative">
        <DrawingCard
          isSuperAdmin={isSuperAdmin}
          drawing={drawing}
          testId={`my-desk-unfiled-thumb-${drawing.id}`}
          className="w-full h-full object-cover"
          fallbackIconClassName="w-8 h-8 text-gray-400"
        />
        <Badge className="absolute top-1 left-1 text-[9px] bg-[#70170F] text-white z-10">unfiled</Badge>
      </div>
      <div className="p-2">
        <p className="text-xs truncate">{drawing.title}</p>
        <Button
          size="sm" variant="outline" className="w-full mt-2 h-7 text-xs"
          onClick={onFile}
          data-testid={`my-desk-plan-room-file-btn-${drawing.id}`}
        >
          <FilePlus2 className="w-3 h-3 mr-1" /> File into set
        </Button>
      </div>
    </div>
  );
}


function PickProjectDialog({ projects, onClose, onPick }) {
  const [pid, setPid] = useState('');
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="my-desk-plan-room-pick-project-dialog">
        <DialogHeader>
          <DialogTitle>Pick a project</DialogTitle>
          <DialogDescription>Which project do these files belong to? They&apos;ll be added as <b>unfiled</b>.</DialogDescription>
        </DialogHeader>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger data-testid="my-desk-plan-room-pick-project-select"><SelectValue placeholder="Choose project" /></SelectTrigger>
          <SelectContent>
            {projects.map(p => (
              <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => pid && onPick(pid)} disabled={!pid}
            className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            data-testid="my-desk-plan-room-pick-project-confirm-btn"
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FileIntoSetDialog({ projectId, drawing, parsed, phases, projects, unfiledId, title, onClose, onDone }) {
  const isTenantPromote = !!unfiledId;
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  const [setName, setSetName] = useState(parsed?.set_name || '');
  const [phase, setPhase] = useState('CD');
  const [discipline, setDiscipline] = useState((parsed?.discipline || '').toLowerCase() || 'architectural');
  const [drawingType, setDrawingType] = useState('floor_plan');
  const [issueDate, setIssueDate] = useState(parsed?.issue_date || new Date().toISOString().slice(0, 10));
  const [revisionCode, setRevisionCode] = useState(parsed?.revision_code || '0');
  const [drawingNumber, setDrawingNumber] = useState(parsed?.sheet_number || drawing?.drawing_number || '');
  const [numberEdited, setNumberEdited] = useState(!!(parsed?.sheet_number || drawing?.drawing_number));
  const [busy, setBusy] = useState(false);

  // Controlled lists — MUST match /app/frontend/src/pages/DrawingControlPage.js
  const DISCIPLINE_OPTIONS = [
    { value: 'architectural', label: 'Architectural (A)' },
    { value: 'structural', label: 'Structural (S)' },
    { value: 'electrical', label: 'Electrical (E)' },
    { value: 'plumbing', label: 'Plumbing (P)' },
    { value: 'mechanical', label: 'Mechanical (M)' },
    { value: 'civil', label: 'Civil (C)' },
    { value: 'interior', label: 'Interior (I)' },
    { value: 'landscape', label: 'Landscape (L)' },
  ];
  const DRAWING_TYPE_OPTIONS = [
    { value: 'floor_plan', label: 'Floor Plan' },
    { value: 'elevation_exterior', label: 'Elevation - Exterior' },
    { value: 'elevation_interior', label: 'Elevation - Interior' },
    { value: 'electrical_plan', label: 'Electrical Plan' },
    { value: 'cabinet_layout', label: 'Cabinet Layout' },
    { value: 'roof_plan', label: 'Roof Plan' },
    { value: 'foundation_plan', label: 'Foundation Plan' },
    { value: 'site_plan', label: 'Site Plan' },
    { value: 'section', label: 'Section' },
    { value: 'detail', label: 'Detail' },
    { value: 'schedule', label: 'Schedule' },
    { value: 'mood_board', label: 'Mood Board' },
    { value: 'other', label: 'Other' },
  ];

  // Auto-suggest next-in-discipline number when a project + discipline are
  // both known and the user hasn't hand-typed a number yet.
  useEffect(() => {
    if (!isTenantPromote || !selectedProject || !discipline || numberEdited) return;
    axios.get(`${API}/projects/${selectedProject}/drawings/next-number`, { params: { discipline } })
      .then((r) => setDrawingNumber(r.data?.next_number || ''))
      .catch(() => {});
  }, [isTenantPromote, selectedProject, discipline, numberEdited]);

  const submit = async () => {
    // Tenant-unfiled promotion path (Build 13.6, transactional).
    if (isTenantPromote) {
      if (!selectedProject) return toast.error('Pick a project to file into');
      if (!drawingNumber.trim()) return toast.error('Drawing number required');
      setBusy(true);
      try {
        await axios.post(`${API}/plan-room/unfiled/${unfiledId}/promote`, {
          project_id: selectedProject,
          drawing_number: drawingNumber.trim(),
          title: title || setName || drawingNumber,
          discipline: discipline || null,
          drawing_type: drawingType || null,
          revision_code: revisionCode,
          issue_date: issueDate,
          phase,
        });
        onDone();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Promote failed');
      } finally { setBusy(false); }
      return;
    }
    // Legacy per-project file-into-set path (kept for existing per-project unfiled).
    if (!setName.trim() || !issueDate) return toast.error('Set name and issue date required');
    setBusy(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/plan-room/unfiled/${drawing.id}/file-into-set`,
        { set_name: setName.trim(), phase, issue_date: issueDate,
          revision_code: revisionCode, drawing_number: drawingNumber.trim() || null }
      );
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to file into set');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="my-desk-plan-room-file-into-set-dialog">
        <DialogHeader>
          <DialogTitle>File into set</DialogTitle>
          <DialogDescription>
            Promote this drawing from unfiled into an issued set. Same-name+phase auto-supersedes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isTenantPromote && (
            <div>
              <Label>Project *</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="promote-project-select"><SelectValue placeholder="Choose project…" /></SelectTrigger>
                <SelectContent>
                  {(projects || []).map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isTenantPromote && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discipline *</Label>
                <Select value={discipline} onValueChange={(v) => { setDiscipline(v); setNumberEdited(false); }}>
                  <SelectTrigger data-testid="promote-discipline-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {DISCIPLINE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={drawingType} onValueChange={setDrawingType}>
                  <SelectTrigger data-testid="promote-drawing-type-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {DRAWING_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {parsed?.set_name && (
            <div className={`text-xs px-3 py-2 rounded bg-green-50 border border-green-200 text-green-900`}>
              Parsed from filename: <b>{parsed.set_name}</b>
              {parsed.revision_code && <> · Rev <b>{parsed.revision_code}</b></>}
              {parsed.issue_date && <> · <b>{parsed.issue_date}</b></>}
            </div>
          )}
          <div>
            <Label>Sheet number *</Label>
            <Input
              value={drawingNumber}
              onChange={(e) => { setDrawingNumber(e.target.value); setNumberEdited(true); }}
              placeholder="A-101"
              data-testid="my-desk-plan-room-file-sheet-input"
            />
          </div>
          {!isTenantPromote && (
            <div>
              <Label>Set name *</Label>
              <Input value={setName} onChange={(e) => setSetName(e.target.value)} data-testid="my-desk-plan-room-file-set-name-input" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phase</Label>
              <Select value={phase} onValueChange={setPhase}>
                <SelectTrigger data-testid="my-desk-plan-room-file-phase-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {phases.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rev</Label>
              <Input value={revisionCode} onChange={(e) => setRevisionCode(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Issue date *</Label>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="my-desk-plan-room-file-date-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy}
            className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            data-testid="my-desk-plan-room-file-submit-btn"
          >
            {busy ? 'Filing…' : 'File into set'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
